import { useCallback } from 'react';

import { Text, Section, Picture, Grid, GridItem } from '@farmersdog/corgi-x';

import { trackRecipesViewedHowItWorks } from '../../../analytics/events';
import { arrowIconSources } from '../assets/arrowIcon';
import { secondBoxSources } from '../assets/secondBox';
import { trialPeriodSources } from '../assets/trialPeriod';
import { useOnElementIntersection } from '../hooks/useOnElementIntersection';

import styles from './GettingStarted.module.css';
import { GettingStartedCard } from './GettingStartedCard/';
import { PopularPlanOptions } from './PopularPlanOptions/';

interface GettingStartedProps {
  petName: string;
  /** Trial daily price with discount, already formatted with $ */
  petDailyDiscountedSubtotal: string;
  /** Trial daily price without discount, already formatted with $ */
  petDailySubtotal: string;
  discountPercentage: number | null;
  /** 'Capped' regular order days of food */
  orderPriceOptimizedRegularOrderSize: number | null | undefined;
  /** Full-box, 'uncapped' regular order days of food */
  dailyPriceOptimizedRegularOrderSize: number | null | undefined;
  /** 'Capped' experimental daily price (for regular order), in dollars and cents */
  orderPriceOptimizedRegularOrderDailyPrice: number | null;
}

/** "Getting Started" section of the Signup Recipes page */
export function GettingStarted({
  petName,
  petDailySubtotal,
  petDailyDiscountedSubtotal,
  discountPercentage,
  orderPriceOptimizedRegularOrderSize,
  dailyPriceOptimizedRegularOrderSize,
  orderPriceOptimizedRegularOrderDailyPrice,
}: GettingStartedProps) {
  const formattedOrderPriceOptimizedRegularOrderDailyPrice = `$${orderPriceOptimizedRegularOrderDailyPrice?.toFixed(2)}`;
  const trialOrderBadges = discountPercentage
    ? ['Bulk Discount', `${discountPercentage}% Discount`]
    : ['Bulk Discount'];

  const orderPriceOptimizedRegularOrderDailyPriceInCents =
    orderPriceOptimizedRegularOrderDailyPrice
      ? Math.round(orderPriceOptimizedRegularOrderDailyPrice * 100)
      : null;
  const dailyPriceOptimizedRegularOrderPriceInCents = petDailySubtotal
    ? priceStringToCents(petDailySubtotal)
    : null;

  const gettingStartedWrapperElementRef = useOnElementIntersection(
    useCallback(() => {
      trackRecipesViewedHowItWorks({
        oldDailyPlanPriceAmount: dailyPriceOptimizedRegularOrderPriceInCents,
        oldOrderSize: dailyPriceOptimizedRegularOrderSize,
        newDailyPlanPriceAmount:
          orderPriceOptimizedRegularOrderDailyPriceInCents,
        newOrderSize: orderPriceOptimizedRegularOrderSize,
      });
    }, [
      dailyPriceOptimizedRegularOrderPriceInCents,
      dailyPriceOptimizedRegularOrderSize,
      orderPriceOptimizedRegularOrderDailyPriceInCents,
      orderPriceOptimizedRegularOrderSize,
    ])
  );

  return (
    <Section
      aria-labelledby="Getting Started"
      className={styles.gettingStartedContainer}
      vSpacing="lg"
    >
      <Text
        className={styles.eyebrow}
        as="h3"
        variant="heading-12"
        align="center"
        bottomSpacing="xs"
        bold
      >
        How it Works
      </Text>
      <div ref={gettingStartedWrapperElementRef}>
        <Text as="h2" align="center" variant="heading-40" bold>
          Getting Started
        </Text>
      </div>
      <Grid
        alignItems="stretch"
        justifyContent="center"
        flexDirection={{
          xs: 'column',
          lg: 'row',
        }}
        columnGap="md"
        rowGap="md"
        topSpacing={{
          xs: 'md',
          lg: 'lg',
        }}
        className={styles.cardContainer}
      >
        <GridItem sm={12} md={12} lg={3}>
          <GettingStartedCard
            badges={trialOrderBadges}
            headline="Trial Period"
            imageSources={trialPeriodSources}
            imageAltText="Trial box with packs"
            listItems={[
              `Learn ${petName}’s favorite recipes`,
              `Transition ${petName} to personalized portions`,
            ]}
            strikeoutPrice={`${petDailySubtotal}/day`}
            actualPrice={`${petDailyDiscountedSubtotal}/day`}
          />
        </GridItem>
        <GridItem sm={12} md={12} lg={3}>
          <GettingStartedCard
            badges={['Bulk Discount']}
            headline="Second Box"
            imageSources={secondBoxSources}
            imageAltText="Feeding second box to dogs"
            listItems={[
              `Ensure you're feeding ${petName}’s favorite recipe`,
              'Adjust your plan and delivery details',
            ]}
            actualPrice={`${petDailySubtotal}/day`}
          />
        </GridItem>
        <div className={styles.arrowWrapper}>
          <Picture
            className={styles.arrowIcon}
            alt="arrow"
            sources={arrowIconSources}
          />
        </div>
        {orderPriceOptimizedRegularOrderSize &&
          dailyPriceOptimizedRegularOrderSize &&
          formattedOrderPriceOptimizedRegularOrderDailyPrice && (
            <GridItem sm={12} md={12} lg={3}>
              <PopularPlanOptions
                preselectedDailyPrice={
                  formattedOrderPriceOptimizedRegularOrderDailyPrice
                }
                otherOptionDailyPrice={petDailySubtotal}
                preselectedDaysOfFood={orderPriceOptimizedRegularOrderSize}
                otherOptionDaysOfFood={dailyPriceOptimizedRegularOrderSize}
              />
            </GridItem>
          )}
      </Grid>
    </Section>
  );
}

/** Converts a price string (e.g., "$3.94") to cents (e.g., 394) */
function priceStringToCents(price: string): number {
  return Math.round(parseFloat(price.replace('$', '')) * 100);
}
